import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Popper from '@material-ui/core/Popper';
import clsx from 'clsx';
import _ from 'lodash';
import React, { useCallback } from 'react';

import Link from 'Components/common/Functions/Select/Triggers/Link/Link';
import Option from './Option/Option';
import Input from './Triggers/Input/Input';
import Navigation from './Triggers/Navigation/Navigation';

const NO_RESULTS_FOUND = 'No Results Found';

const getEmptyMessage = (emptyMessage) => {
  return emptyMessage ? emptyMessage : NO_RESULTS_FOUND;
};

const getOptions = ({ options, placeholder, emptyMessage, firstIsNull = false }) => {
  let emptyOption = [];

  if (firstIsNull) {
    emptyOption.push({
      value: null,
      label: options.length > 0 ? placeholder : getEmptyMessage(emptyMessage),
    });
  }

  return [...emptyOption, ...options];
};

const FirstOption = ({ firstOption, onClose }) => {
  const handleChange = useCallback(
    (e) => {
      if (firstOption.onChange) {
        firstOption.onChange(e);
      }

      onClose();
    },
    [firstOption.onChange, onClose]
  );

  const onClick = firstOption.isSelectable ? handleChange : null;

  return (
    <div className="selectOption" onClick={onClick}>
      <div className="optionTrigger">{firstOption.Component({ onClick: onClose })}</div>
    </div>
  );
};

class Single extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      options: [],
      selected: {},
      anchorEl: null,
      showSelected: true,
      inputValue: '',
      index: 0,
      initialSelectedValue: undefined, // Track initial selected value
      hasSelectionChanged: false,      // Track if selection has changed
    };

    _.map(['scrollTop', 'scrollDown'], (fn) => {
      this[fn] = this[fn].bind(this);
    });
  }

  isInitialValueEmpty() {
    const initial = this.state.initialSelectedValue;
    return initial === null || initial === '' || typeof initial === 'undefined';
  }

  componentDidMount() {
    const updatedOptions = getOptions({
      options: this.props.options,
      placeholder: this.props.placeholder,
      emptyMessage: this.props.emptyMessage,
      firstIsNull: this.props.firstIsNull,
    });

    const initialSelected =
    (this.props.selected || {}).value
      ? this.props.selected
      : this.props.options.length > 0 && this.props.isFirstItemSelected
      ? updatedOptions[0]
      : {};

    this.setState({
      ...this.state,
            selected: initialSelected,
      options: updatedOptions,
      initialSelectedValue: initialSelected.value, // Set initial selected value
    });
  }

  // Track if selection has changed from the initial value
  componentDidUpdate(prevProps) {
    const { options, selected, placeholder, emptyMessage, firstIsNull, isClose, retainSelected } = this.props;
    const { initialSelectedValue, hasSelectionChanged } = this.state;
  
    // Handle props.options or props.selected changes
    const optionsChanged = !_.isEqual(options, prevProps.options);
    const selectedChanged = !_.isEqual(selected, prevProps.selected);
  
    if (optionsChanged || selectedChanged) {
      const updatedOptions = getOptions({ options, placeholder, emptyMessage, firstIsNull });
  
      this.setState({
        options: updatedOptions,
        selected: selected?.value ? selected : {},
      });
    }
  
    // Handle close trigger
    if (isClose && isClose !== prevProps.isClose) {
      this.setState({
        selected: retainSelected ? selected : {},
        anchorEl: null,
      });
    }
  
    // Detect selection change from initial value
    if (typeof initialSelectedValue !== 'undefined') {
      const currentValue = this.state.selected?.value;
  
      const isInitialEmpty =
        initialSelectedValue === null ||
        initialSelectedValue === '' ||
        typeof initialSelectedValue === 'undefined';
  
      const hasChanged =
        (!hasSelectionChanged &&
          ((isInitialEmpty && currentValue) ||
            (!isInitialEmpty && currentValue !== initialSelectedValue))) ||
        (hasSelectionChanged && currentValue === initialSelectedValue);
  
      if (hasChanged) {
        this.setState({ hasSelectionChanged: !hasSelectionChanged });
      }
    }
  }

  shouldComponentUpdate(nextProps) {
    const updatedOptions = getOptions({
      options: nextProps.options,
      placeholder: nextProps.placeholder,
      emptyMessage: nextProps.emptyMessage,
      firstIsNull: nextProps.firstIsNull,
    });

    if (
      !_.isEqual(nextProps.options, this.props.options) ||
      !_.isEqual(nextProps.selected, this.props.selected)
    ) {
      this.setState({
        ...this.state,
        ...{
          options: updatedOptions,
          ...((nextProps.selected || {}).value
            ? { selected: nextProps.selected }
            : { selected: {} }),
        },
      });
    }
    if (!_.isEqual(nextProps.isClose, this.props.isClose) && nextProps.isClose === true) {
      this.setState({
        ...this.state,
        selected: nextProps.retainSelected ? nextProps.selected : {},
        anchorEl: null,
      });
    }

    return true;
  }

  scrollTop() {
    const { index } = this.state;
    const idx = index - 3;

    if (this.refs[`select_option_${idx}`]) {
      this.refs[`select_option_${idx}`].scrollIntoView();

      this.setState({ index: idx }, () => {
        if (Object.keys(this.refs).length - idx > 5) {
          $('#scrollDown').show();
        }
        if (idx < 1) {
          $('#scrollTop').hide();
        }
      });
    }
  }

  scrollDown() {
    const { index } = this.state;
    const idx = index + 3;

    if (this.refs[`select_option_${idx}`]) {
      this.refs[`select_option_${idx}`].scrollIntoView();

      this.setState({ index: idx }, () => {
        if (Object.keys(this.refs).length - idx <= 5) {
          $('#scrollDown').hide();
        }
        if (idx > 0) {
          $('#scrollTop').show();
        }
      });
    }
  }

  handleClick = (event) => {
    this.setState({ ...this.state, anchorEl: event.currentTarget });

    if (this.props.onClick) {
      this.props.onClick(event);
    }
  };

  handleClose = () => {
    this.setState({ ...this.state, anchorEl: null, index: 0 });
  };

  handleStateUpdate = (update, cb) => {
    if (!this.props.isDropDown) {
      this.setState({ ...this.state, ...update }, cb);
    } else {
      cb();
    }
  };

  render() {
    const { props, state } = this;
    const { emptyMessage = NO_RESULTS_FOUND, isBackspaceClearEnabled = true } = this.props;
    const open = Boolean(state.anchorEl);
    const isNav = props.type && props.type === 'navigation';
    const onTrigger = !props.disabled ? this.handleClick : null;
    const myEmptyMessage = getEmptyMessage(emptyMessage);
    const { firstOption } = props;

    // Check if field is created with empty default values. 
    const initialIsEmpty = this.isInitialValueEmpty();

    return (
      <div
        className={clsx(
          'selectContainer',
          props.classes.main,
          props.isSearchable && 'searchable',
          props.disabled && 'tw-cursor-not-allowed'
        )}
        data-tip
        data-tooltip-id={props.tooltipID}
      >
        <div className="selectedValue">
          {isNav && (
            <Navigation
              open={open}
              selected={state.selected}
              onTrigger={onTrigger}
              className={props.classes.trigger}
              nextAction={props.nextAction}
              prevAction={props.prevAction}
              placeholder={this.props.placeholder}
              useValue={props.useValue}
            />
          )}
          {!props.isSearchable && !isNav && (
            <Link
              type={props.type}
              open={open}
              selected={state.selected}
              onTrigger={onTrigger}
              className={props.classes.trigger}
              placeholder={this.props.placeholder}
              useValue={props.useValue}
              renderSelected={props.renderSelected}
              classes={props.classes}
            />
          )}
          {props.isSearchable && !isNav && (
            <Input
              id={props.id}
              state={state}
              open={open}
              options={props.options}
              placeholder={props.placeholder}
              showSelectedInInput={props.showSelectedInInput}
              renderSelected={props.renderSelected}
              onTrigger={onTrigger}
              className={props.classes.trigger}
              onUpdateState={this.handleStateUpdate}
              onOpen={props.onOpen}
              onSearch={props.onSearch}
              onClearSelected={props.onClearSelected}
              value={this.state.inputValue}
              searchIcon={props.searchIcon}
              selectedSpanClasses={props.selectedSpanClasses}
              clearIcon={props.clearIcon}
              disabled={props.disabled}
              isAiGenerated={props.isAiGenerated}
              isManualExpensePrefilled={props.isManualExpensePrefilled && props.selected !== null && !initialIsEmpty}
              hasSelected={props.selected !== null }
              isBackspaceClearEnabled={isBackspaceClearEnabled}
              hasSelectionChanged={state.hasSelectionChanged} 
            />
          )}
        </div>
        <Popper
          open={open}
          anchorEl={state.anchorEl}
          placement="bottom-start"
          style={{ zIndex: 9999 }}
          disablePortal={true}
          modifiers={{
            preventOverflow: {
              enabled: true,
              escapeWithReference: true,
              boundariesElement: 'viewport',
            },
          }}
        >
          <ClickAwayListener onClickAway={this.handleClose}>
            <div className={`selectOptions ${props.classes.options}`}>
              {props.hasScroll && (
                <div className="scrollButtons text-center" id="scrollTop" onClick={this.scrollTop}>
                  <i className="material-icons">expand_less</i>
                </div>
              )}
              <div className={` scrollContainer ${props.hasScroll ? ' active' : ''}`}>
                {firstOption && (
                  <FirstOption
                    firstOption={firstOption}
                    onClose={this.handleClose}
                    onChange={props.onChange}
                  />
                )}

                {state.options.length > 0 &&
                  !props.forceHideOption &&
                  !props.isLoading &&
                  state.options
                    .filter(
                      (option) =>
                        ((!props.showSelectedInOptions && option.value !== state.selected.value) ||
                          props.showSelectedInOptions) &&
                        props.exclude.indexOf(option.value) === -1
                    )
                    .map((option, i) => {
                      return (
                        <div
                          className="optionContainer"
                          ref={`select_option_${i}`}
                          key={`selectOptionKey_${option.value}`}
                        >
                          <Option
                            key={`selectOptionKey_${option.value}`}
                            option={option}
                            selected={state.selected}
                            closeOnChange={props.closeOnChange}
                            renderOption={props.renderOption}
                            onUpdateState={this.handleStateUpdate}
                            onChange={props.onChange}
                            onSelectOption={props.onSelectOption}
                            onClose={this.handleClose}
                            link={props.link}
                            showActiveIcon={props.showActiveIcon}
                          />
                        </div>
                      );
                    })}
                {this.state.options.length === 0 && !props.isLoading && (
                  <div className="selectOption">
                    <div className="optionTrigger">
                      <span>{myEmptyMessage}</span>
                    </div>
                  </div>
                )}
                {props.isLoading && (
                  <div className="selectOption">
                    <div className="optionTrigger">
                      <span>Loading...</span>
                    </div>
                  </div>
                )}
                {props.bottomContent && (
                  <div className="selectBottomContent">{props.bottomContent}</div>
                )}
              </div>
              {props.hasScroll && (
                <div
                  className="scrollButtons text-center"
                  id="scrollDown"
                  onClick={this.scrollDown}
                >
                  <i className="material-icons">expand_more</i>
                </div>
              )}
            </div>
          </ClickAwayListener>
        </Popper>
      </div>
    );
  }
}

Single.defaultProps = {
  options: [{ value: '', label: 'No options' }],
  closeOnChange: true,
  classes: {
    main: '',
    options: '',
  },
  selected: {},
  showSelectedInInput: true,
  showSelectedInOptions: true,
  exclude: [],
  placeholder: 'Select',
  isFirstItemSelected: true,
  isAiGenerated: false,
  showActiveIcon: false,
};

export default Single;
